/**
 * Composable для работы с WebRTC DataChannel
 * Отвечает за:
 * - Создание и управление DataChannel
 * - Отправку/приём мета-информации о состоянии медиа
 * - Отслеживание состояния удаленного медиа
 */

import { ref, type Ref } from 'vue';

export interface DataChannelMessage {
  type: string;
  payload: any;
}

export function useDataChannel() {
  // ==================== СОСТОЯНИЕ ====================

  /** DataChannel для мета-информации */
  const dataChannel: Ref<RTCDataChannel | null> = ref(null);

  /** Состояние удаленного медиа (из dataChannel и onmute) */
  const remoteVideoMuted: Ref<boolean> = ref(false);
  const remoteAudioMuted: Ref<boolean> = ref(false);

  /** Колбэк для обработки сообщений из dataChannel */
  let messageCallback: ((message: DataChannelMessage) => void) | null = null;

  // ==================== ОБРАБОТЧИКИ ====================

  /**
   * Обрабатывает входящее сообщение из dataChannel
   */
  function handleMessage(message: DataChannelMessage): void {
    console.log('📨 DataChannel message:', message);

    // Обрабатываем сообщение о состоянии медиа
    if (message.type === 'media-state') {
      if (message.payload.videoMuted !== undefined) {
        remoteVideoMuted.value = message.payload.videoMuted;
      }
      if (message.payload.audioMuted !== undefined) {
        remoteAudioMuted.value = message.payload.audioMuted;
      }
    }

    // Вызываем внешний колбэк если установлен
    if (messageCallback) {
      messageCallback(message);
    }
  }

  /**
   * Настраивает обработчики для DataChannel
   */
  function setupDataChannelHandlers(dc: RTCDataChannel): void {
    dc.onopen = () => {
      console.log('✅ DataChannel открыт');
      dataChannel.value = dc;
    };

    dc.onclose = () => {
      console.log('⚠️ DataChannel закрыт');
      dataChannel.value = null;
    };

    dc.onerror = (error) => {
      console.error('❌ DataChannel error:', error);
    };

    dc.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as DataChannelMessage;
        handleMessage(message);
      } catch (error) {
        console.error('❌ Ошибка парсинга сообщения dataChannel:', error);
      }
    };
  }

  // ==================== СОЗДАНИЕ И ИНИЦИАЛИЗАЦИЯ ====================

  /**
   * Создает DataChannel для мета-информации
   */
  function createDataChannel(peerConnection: RTCPeerConnection): void {
    try {
      const dc = peerConnection.createDataChannel('media-state', {
        ordered: true
      });

      setupDataChannelHandlers(dc);
      dataChannel.value = dc;
      console.log('✅ DataChannel создан');
    } catch (error) {
      console.error('❌ Ошибка создания dataChannel:', error);
    }
  }

  /**
   * Настраивает обработчик входящих DataChannel от другого пира
   */
  function setupIncomingDataChannel(peerConnection: RTCPeerConnection): void {
    peerConnection.ondatachannel = (event) => {
      const dc = event.channel;
      console.log('📥 Входящий dataChannel:', dc.label);

      if (dc.label === 'media-state') {
        setupDataChannelHandlers(dc);
      }
    };
  }

  /**
   * Инициализирует DataChannel для PeerConnection
   * Создает исходящий и настраивает обработчик входящих
   */
  function initializeDataChannel(peerConnection: RTCPeerConnection): void {
    createDataChannel(peerConnection);
    setupIncomingDataChannel(peerConnection);
  }

  // ==================== ОТПРАВКА СООБЩЕНИЙ ====================

  /**
   * Отправляет сообщение через DataChannel
   */
  function sendMessage(message: DataChannelMessage): boolean {
    if (!dataChannel.value || dataChannel.value.readyState !== 'open') {
      console.warn('⚠️ DataChannel не открыт для отправки сообщения');
      return false;
    }

    try {
      dataChannel.value.send(JSON.stringify(message));
      console.log('📤 Сообщение отправлено через DataChannel:', message);
      return true;
    } catch (error) {
      console.error('❌ Ошибка отправки сообщения через DataChannel:', error);
      return false;
    }
  }

  /**
   * Отправляет состояние медиа через dataChannel
   */
  function sendMediaState(videoMuted?: boolean, audioMuted?: boolean): boolean {
    const message: DataChannelMessage = {
      type: 'media-state',
      payload: {
        ...(videoMuted !== undefined && { videoMuted }),
        ...(audioMuted !== undefined && { audioMuted })
      }
    };

    return sendMessage(message);
  }

  // ==================== УПРАВЛЕНИЕ ====================

  /**
   * Устанавливает колбэк для обработки сообщений из dataChannel
   */
  function onMessage(callback: (message: DataChannelMessage) => void): void {
    messageCallback = callback;
  }

  /**
   * Сбрасывает состояние удаленного медиа
   */
  function resetRemoteMediaState(): void {
    remoteVideoMuted.value = false;
    remoteAudioMuted.value = false;
  }

  /**
   * Обновляет состояние удаленного медиа из событий mute/unmute
   * @param track - трек который изменил состояние
   * @param muted - явное состояние muted (если не указано, берется из track.muted)
   */
  function updateRemoteMediaFromTrackMute(track: MediaStreamTrack, muted?: boolean): void {
    const isMuted = muted !== undefined ? muted : track.muted;
    
    if (track.kind === 'video') {
      remoteVideoMuted.value = isMuted;
    } else if (track.kind === 'audio') {
      remoteAudioMuted.value = isMuted;
    }
  }

  /**
   * Очищает ресурсы
   */
  function cleanup(): void {
    if (dataChannel.value) {
      dataChannel.value.close();
      dataChannel.value = null;
    }
    resetRemoteMediaState();
    messageCallback = null;
  }

  // ==================== ВОЗВРАЩАЕМЫЙ API ====================

  return {
    // Состояние
    dataChannel,
    remoteVideoMuted,
    remoteAudioMuted,

    // Инициализация
    initializeDataChannel,
    createDataChannel,
    setupIncomingDataChannel,

    // Отправка сообщений
    sendMessage,
    sendMediaState,

    // Управление
    onMessage,
    resetRemoteMediaState,
    updateRemoteMediaFromTrackMute,

    // Очистка
    cleanup
  };
}

